/**
 * MRP Lite Data Management Hook
 * 
 * Central hook for all Mini-MRP Lite data operations:
 * - Dashboard metrics
 * - MRP run history
 * - Running MRP calculations
 * - Managing planned orders
 * - Shortage diff comparisons
 */

import { useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase/client';
import { useAuthStore } from '@/store/authStore';
import { useToast } from '@/components/ui/use-toast';
import {
  emitMRPRunStarted,
  emitMRPRunCompleted,
  emitMRPShortageDetected,
  emitMRPSuggestionAccepted,
  emitMRPPlannedOrderConverted,
} from '@/lib/events/mrpEvents';

import {
  MRPDashboardMetrics,
  MRPHistoryEntry,
  MRPRun,
  MRPShortage,
  PlannedOrder,
  MRPRunRequest,
  AcceptSuggestionsRequest,
  ConvertPlannedOrderRequest,
  CancelPlannedOrderRequest,
  ShortageDiff,
  ShortageDiffComparison,
  PlannedOrdersFilters,
  ItemPlanningParams,
  UpdateItemPlanningParamsRequest
} from '@/types/mrpLite';

export function useMRPLite() {
  const { user } = useAuthStore();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  // ==================== Warehouse Operations ====================

  const fetchWarehouses = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('warehouse')
        .select('id, code, name, type, is_active')
        .eq('is_active', true)
        .order('name', { ascending: true });

      if (error) throw error;

      return data?.map(warehouse => ({
        id: warehouse.id,
        code: warehouse.code,
        name: warehouse.name,
        type: warehouse.type
      })) || [];

    } catch (error) {
      console.error('Error fetching warehouses:', error);
      toast({
        title: 'Error',
        description: 'Failed to load warehouses',
        variant: 'destructive',
      });
      throw error;
    }
  }, [toast]);

  // ==================== Dashboard Metrics ====================

  const fetchDashboardMetrics = useCallback(async (): Promise<MRPDashboardMetrics> => {
    try {
      setIsLoading(true);

      // Get last run info
      const { data: lastRun } = await supabase
        .from('mrp_runs')
        .select('run_number, completed_at, status')
        .eq('status', 'COMPLETED')
        .order('completed_at', { ascending: false })
        .limit(1)
        .single();

      // Get planned orders metrics
      const { data: plannedOrdersData } = await supabase
        .from('planned_orders')
        .select('status')
        .not('status', 'eq', 'CANCELLED');

      const plannedOrdersMetrics = plannedOrdersData?.reduce(
        (acc, order) => {
          acc.total_planned++;
          if (order.status === 'ACCEPTED') acc.total_accepted++;
          if (order.status === 'CONVERTED') acc.total_converted++;
          return acc;
        },
        { total_planned: 0, total_accepted: 0, total_converted: 0 }
      ) || { total_planned: 0, total_accepted: 0, total_converted: 0 };

      // Get projected stock-outs (shortages with critical dates)
      const { data: criticalShortages } = await supabase
        .from('mrp_shortages')
        .select('need_by_date, is_critical')
        .eq('status', 'OPEN');

      const now = new Date();
      const next14Days = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000);
      const next30Days = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

      const projectedStockouts = criticalShortages?.reduce(
        (acc, shortage) => {
          const needByDate = new Date(shortage.need_by_date);
          if (needByDate <= next14Days) acc.next_14_days++;
          if (needByDate <= next30Days) acc.next_30_days++;
          return acc;
        },
        { next_14_days: 0, next_30_days: 0 }
      ) || { next_14_days: 0, next_30_days: 0 };

      // Calculate total shortage value (simplified - would need item costs)
      const { data: shortageData } = await supabase
        .from('mrp_shortages')
        .select('net_shortage_qty')
        .eq('status', 'OPEN');

      const totalShortageValue = shortageData?.reduce(
        (sum, shortage) => sum + (shortage.net_shortage_qty * 100), // Placeholder calc
        0
      ) || 0;

      return {
        last_run: lastRun ? {
          run_number: lastRun.run_number,
          completed_at: lastRun.completed_at,
          status: lastRun.status
        } : undefined,
        planned_orders: plannedOrdersMetrics,
        projected_stockouts: projectedStockouts,
        total_shortage_value: totalShortageValue
      };

    } catch (error) {
      console.error('Error fetching dashboard metrics:', error);
      toast({
        title: 'Error',
        description: 'Failed to load dashboard metrics',
        variant: 'destructive',
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  // ==================== MRP History ====================

  const fetchMRPHistory = useCallback(async (): Promise<MRPHistoryEntry[]> => {
    try {
      const { data, error } = await supabase
        .from('mrp_runs')
        .select(`
          id,
          run_number,
          horizon_date,
          warehouse:warehouse_id(name),
          total_shortages_found,
          run_duration_seconds,
          completed_at,
          status
        `)
        .order('completed_at', { ascending: false })
        .limit(20);

      if (error) throw error;

      return data?.map(run => ({
        id: run.id,
        run_number: run.run_number,
        horizon_date: run.horizon_date,
        warehouse_name: run.warehouse?.name || 'Unknown',
        total_shortages: run.total_shortages_found,
        run_duration_seconds: run.run_duration_seconds,
        completed_at: run.completed_at,
        status: run.status
      })) || [];

    } catch (error) {
      console.error('Error fetching MRP history:', error);
      toast({
        title: 'Error',
        description: 'Failed to load MRP history',
        variant: 'destructive',
      });
      throw error;
    }
  }, [toast]);

  // ==================== MRP Operations ====================

  const runMRP = useCallback(async (request: MRPRunRequest): Promise<string> => {
    if (!user) throw new Error('User not authenticated');

    try {
      setIsLoading(true);

      // Generate unique run number
      const runNumber = `MRP-${new Date().getFullYear()}${String(new Date().getMonth() + 1).padStart(2, '0')}${String(new Date().getDate()).padStart(2, '0')}-${String(Math.floor(Math.random() * 1000)).padStart(3, '0')}`;

      // Create MRP run record
      const { data: mrpRun, error: runError } = await supabase
        .from('mrp_runs')
        .insert({
          run_number: runNumber,
          horizon_date: request.horizon_date,
          warehouse_id: request.warehouse_id,
          include_sales_orders: request.include_sales_orders,
          status: 'PLANNING',
          created_by: user.id
        })
        .select()
        .single();

      if (runError) throw runError;

      // Emit run started event
      emitMRPRunStarted({
        run_id: mrpRun.id,
        run_number: runNumber,
        warehouse_id: request.warehouse_id,
        horizon_date: request.horizon_date,
        created_by: user.id,
        timestamp: new Date().toISOString()
      });

      // Start the calculation simulation
      // In real implementation, this would trigger an edge function
      // or background job to perform actual MRP calculations
      const startTime = Date.now();
      await simulateMRPCalculation(mrpRun.id);
      const duration = Date.now() - startTime;

      // Get shortages count for completion event
      const { data: shortages } = await supabase
        .from('mrp_shortages')
        .select('id')
        .eq('mrp_run_id', mrpRun.id);

      // Emit run completed event
      emitMRPRunCompleted({
        run_id: mrpRun.id,
        run_number: runNumber,
        warehouse_id: request.warehouse_id,
        shortages_found: shortages?.length || 0,
        items_analyzed: 100, // Mock value - would be actual count in real implementation
        duration_ms: duration,
        timestamp: new Date().toISOString()
      });

      toast({
        title: 'Success',
        description: `MRP run ${runNumber} completed successfully`,
      });

      return mrpRun.id;

    } catch (error) {
      console.error('Error running MRP:', error);
      toast({
        title: 'Error',
        description: 'Failed to run MRP calculation',
        variant: 'destructive',
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [user, toast]);

  // ==================== Shortage Operations ====================

  const fetchShortages = useCallback(async (runId: string): Promise<MRPShortage[]> => {
    try {
      const { data, error } = await supabase
        .from('mrp_shortages')
        .select(`
          *,
          item:item_id!inner(
            id,
            component_name,
            drawing_number,
            material,
            unit
          )
        `)
        .eq('mrp_run_id', runId)
        .order('need_by_date', { ascending: true });

      if (error) throw error;

      return data?.map(shortage => ({
        id: shortage.id,
        mrp_run_id: shortage.mrp_run_id,
        item_id: shortage.item_id,
        item_code: shortage.item?.drawing_number || 'N/A',
        item_name: shortage.item?.component_name || 'Unknown Item',
        unit: shortage.item?.unit || 'Nos',
        material: shortage.item?.material || 'N/A',
        net_shortage_qty: Number(shortage.net_shortage_qty),
        suggested_qty: Number(shortage.suggested_qty),
        need_by_date: shortage.need_by_date,
        order_date: shortage.order_date,
        suggested_type: shortage.suggested_type === 'BUY' ? 'PR' : 'WO',
        lead_time_days: shortage.lead_time_days || 0,
        safety_days: shortage.safety_days || 0,
        is_critical: shortage.is_critical || false,
        status: shortage.status === 'CANCELLED' ? 'IGNORED' : shortage.status as 'OPEN' | 'ACCEPTED',
        created_at: shortage.created_at,
        updated_at: shortage.updated_at
      })) || [];

    } catch (error) {
      console.error('Error fetching shortages:', error);
      toast({
        title: 'Error',
        description: 'Failed to load MRP shortages',
        variant: 'destructive',
      });
      throw error;
    }
  }, [toast]);

  // ==================== Shortage Operations ====================

  const acceptSuggestions = useCallback(async (request: AcceptSuggestionsRequest): Promise<void> => {
    try {
      setIsLoading(true);

      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      // Update shortage status to ACCEPTED
      const { error: updateError } = await supabase
        .from('mrp_shortages')
        .update({ status: 'ACCEPTED' })
        .in('id', request.shortage_ids);

      if (updateError) throw updateError;

      // Create planned orders for each accepted shortage
      const { data: shortages } = await supabase
        .from('mrp_shortages')
        .select('*')
        .in('id', request.shortage_ids);

      if (shortages) {
        const plannedOrders = await Promise.all(
          shortages.map(async (shortage) => {
            const plannedOrderNumber = `PO-${new Date().getFullYear()}-${String(Date.now()).slice(-6)}`;
            
            return {
              planned_order_number: plannedOrderNumber,
              mrp_run_id: shortage.mrp_run_id,
              mrp_shortage_id: shortage.id,
              item_id: shortage.item_id,
              planned_qty: shortage.suggested_qty,
              need_by_date: shortage.need_by_date,
              order_date: shortage.order_date,
              type: shortage.suggested_type,
              status: 'ACCEPTED',
              accepted_at: new Date().toISOString(),
              accepted_by: user.id
            };
          })
        );

        const { error: insertError } = await supabase
          .from('planned_orders')
          .insert(plannedOrders);

        if (insertError) throw insertError;
      }

      toast({
        title: 'Success',
        description: `${request.shortage_ids.length} suggestions accepted and converted to planned orders`,
      });

    } catch (error) {
      console.error('Error accepting suggestions:', error);
      toast({
        title: 'Error',
        description: 'Failed to accept suggestions',
        variant: 'destructive',
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [user, toast]);

  // ==================== Planned Order Operations ====================

  const fetchPlannedOrders = useCallback(async (filters?: PlannedOrdersFilters): Promise<PlannedOrder[]> => {
    try {
      let query = supabase
        .from('planned_orders')
        .select(`
          *,
          item:item_id(id, component_name, part_number, unit),
          mrp_run:mrp_run_id(run_number),
          accepted_by_user:accepted_by(id, full_name),
          converted_by_user:converted_by(id, full_name),
          cancelled_by_user:cancelled_by(id, full_name)
        `)
        .order('created_at', { ascending: false });

      // Apply filters
      if (filters?.status) {
        query = query.eq('status', filters.status);
      }
      if (filters?.type) {
        query = query.eq('type', filters.type);
      }
      if (filters?.search_query) {
        query = query.or(`
          planned_order_number.ilike.%${filters.search_query}%,
          item.component_name.ilike.%${filters.search_query}%
        `);
      }
      if (filters?.need_by_date_from) {
        query = query.gte('need_by_date', filters.need_by_date_from);
      }
      if (filters?.need_by_date_to) {
        query = query.lte('need_by_date', filters.need_by_date_to);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(order => ({
        ...order,
        item_code: order.item?.part_number || '',
        item_name: order.item?.component_name || '',
        accepted_by_user_name: order.accepted_by_user?.full_name || '',
        converted_by_user_name: order.converted_by_user?.full_name || '',
        cancelled_by_user_name: order.cancelled_by_user?.full_name || ''
      })) || [];

    } catch (error) {
      console.error('Error fetching planned orders:', error);
      throw error;
    }
  }, []);

  // ==================== Helper Functions ====================

  const simulateMRPCalculation = async (runId: string): Promise<void> => {
    // Simulate MRP calculation process with progress updates
    // In a real implementation, this would:
    // 1. Analyze BOM requirements
    // 2. Check inventory levels
    // 3. Consider lead times
    // 4. Generate shortage recommendations

    const startTime = Date.now();

    // Simulate progress with status updates
    await supabase
      .from('mrp_runs')
      .update({ status: 'ANALYZING_BOM' })
      .eq('id', runId);
    
    await new Promise(resolve => setTimeout(resolve, 800));

    await supabase
      .from('mrp_runs')
      .update({ status: 'CHECKING_INVENTORY' })
      .eq('id', runId);
    
    await new Promise(resolve => setTimeout(resolve, 800));

    await supabase
      .from('mrp_runs')
      .update({ status: 'GENERATING_SUGGESTIONS' })
      .eq('id', runId);
    
    await new Promise(resolve => setTimeout(resolve, 800));

    // Generate sample shortage data for demonstration
    // In production, this would be replaced with actual MRP logic
    await generateSampleShortages(runId);

    const endTime = Date.now();
    const duration = Math.floor((endTime - startTime) / 1000);

    // Mark run as completed with actual metrics
    const { data: shortageCount } = await supabase
      .from('mrp_shortages')
      .select('id', { count: 'exact' })
      .eq('mrp_run_id', runId);

    await supabase
      .from('mrp_runs')
      .update({
        status: 'COMPLETED',
        completed_at: new Date().toISOString(),
        total_components_analyzed: 25,
        total_shortages_found: shortageCount?.length || 0,
        total_suggestions_created: shortageCount?.length || 0,
        run_duration_seconds: duration
      })
      .eq('id', runId);
  };

  const generateSampleShortages = async (runId: string): Promise<void> => {
    try {
      // Get some sample BOM components to create shortages for
      const { data: components } = await supabase
        .from('bom_components')
        .select('id, component_name, drawing_number, material, unit')
        .eq('component_type', 'raw_material')
        .limit(8);

      if (!components || components.length === 0) {
        console.warn('No BOM components found for shortage generation');
        return;
      }

      const now = new Date();
      const sampleShortages = components.map((component, index) => {
        const needByDate = new Date(now.getTime() + (7 + index * 3) * 24 * 60 * 60 * 1000);
        const orderDate = new Date(needByDate.getTime() - 7 * 24 * 60 * 60 * 1000);
        const shortageQty = 50 + Math.floor(Math.random() * 200);
        
        return {
          mrp_run_id: runId,
          item_id: component.id,
          net_shortage_qty: shortageQty,
          suggested_qty: Math.ceil(shortageQty / 10) * 10, // Round up to nearest 10
          need_by_date: needByDate.toISOString().split('T')[0],
          order_date: orderDate.toISOString().split('T')[0],
          suggested_type: Math.random() > 0.3 ? 'BUY' : 'MAKE',
          lead_time_days: 5 + Math.floor(Math.random() * 10),
          safety_days: Math.floor(Math.random() * 3),
          is_critical: Math.random() > 0.7,
          status: 'OPEN'
        };
      });

      const { error } = await supabase
        .from('mrp_shortages')
        .insert(sampleShortages);

      if (error) {
        console.error('Error inserting sample shortages:', error);
      }

    } catch (error) {
      console.error('Error generating sample shortages:', error);
    }
  };

  // ==================== Item Planning Parameters ====================

  const fetchItemPlanningParams = useCallback(async (itemId: string): Promise<ItemPlanningParams | null> => {
    try {
      const { data, error } = await supabase
        .from('item_planning_params')
        .select('*')
        .eq('item_id', itemId)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      return data || null;
    } catch (error) {
      console.error('Error fetching item planning parameters:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch item planning parameters',
        variant: 'destructive',
      });
      throw error;
    }
  }, [toast]);

  const saveItemPlanningParams = useCallback(async (params: Omit<ItemPlanningParams, 'id' | 'created_at' | 'updated_at'>): Promise<void> => {
    if (!user) throw new Error('User not authenticated');

    try {
      setIsLoading(true);

      const { error } = await supabase
        .from('item_planning_params')
        .upsert({
          ...params,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'item_id'
        });

      if (error) throw error;

    } catch (error) {
      console.error('Error saving item planning parameters:', error);
      toast({
        title: 'Error',
        description: 'Failed to save item planning parameters',
        variant: 'destructive',
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [user, toast]);

  // ==================== Shortage Diff Operations ====================

  const fetchShortageDiff = useCallback(async (currentRunId: string, previousRunId: string): Promise<ShortageDiffComparison> => {
    try {
      // Fetch current run shortages
      const { data: currentShortages, error: currentError } = await supabase
        .from('mrp_shortages')
        .select(`
          *,
          item:item_id!inner(
            id,
            component_name,
            drawing_number,
            unit
          )
        `)
        .eq('mrp_run_id', currentRunId);

      if (currentError) throw currentError;

      // Fetch previous run shortages
      const { data: previousShortages, error: previousError } = await supabase
        .from('mrp_shortages')
        .select(`
          *,
          item:item_id!inner(
            id,
            component_name,
            drawing_number,
            unit
          )
        `)
        .eq('mrp_run_id', previousRunId);

      if (previousError) throw previousError;

      // Fetch run details
      const { data: currentRun } = await supabase
        .from('mrp_runs')
        .select('run_number')
        .eq('id', currentRunId)
        .single();

      const { data: previousRun } = await supabase
        .from('mrp_runs')
        .select('run_number')
        .eq('id', previousRunId)
        .single();

      // Create item lookup maps
      const currentMap = new Map<string, number>();
      const previousMap = new Map<string, number>();
      const itemDetails = new Map<string, { code: string; name: string }>();

      // Process current shortages
      currentShortages?.forEach(shortage => {
        currentMap.set(shortage.item_id, Number(shortage.net_shortage_qty));
        itemDetails.set(shortage.item_id, {
          code: shortage.item?.drawing_number || 'N/A',
          name: shortage.item?.component_name || 'Unknown Item'
        });
      });

      // Process previous shortages
      previousShortages?.forEach(shortage => {
        previousMap.set(shortage.item_id, Number(shortage.net_shortage_qty));
        itemDetails.set(shortage.item_id, {
          code: shortage.item?.drawing_number || 'N/A',
          name: shortage.item?.component_name || 'Unknown Item'
        });
      });

      // Generate differences
      const allItemIds = new Set([...currentMap.keys(), ...previousMap.keys()]);
      const differences: ShortageDiff[] = [];
      let summary = {
        new_shortages: 0,
        resolved_shortages: 0,
        increased_shortages: 0,
        decreased_shortages: 0
      };

      allItemIds.forEach(itemId => {
        const currentQty = currentMap.get(itemId) || 0;
        const previousQty = previousMap.get(itemId) || 0;
        const change = currentQty - previousQty;
        const details = itemDetails.get(itemId)!;

        let changeDirection: 'INCREASED' | 'DECREASED' | 'NEW' | 'RESOLVED';

        if (previousQty === 0 && currentQty > 0) {
          changeDirection = 'NEW';
          summary.new_shortages++;
        } else if (previousQty > 0 && currentQty === 0) {
          changeDirection = 'RESOLVED';
          summary.resolved_shortages++;
        } else if (change > 0) {
          changeDirection = 'INCREASED';
          summary.increased_shortages++;
        } else if (change < 0) {
          changeDirection = 'DECREASED';
          summary.decreased_shortages++;
        } else {
          return; // No change, skip
        }

        differences.push({
          item_id: itemId,
          item_code: details.code,
          item_name: details.name,
          previous_shortage: previousQty,
          current_shortage: currentQty,
          change,
          change_direction: changeDirection
        });
      });

      return {
        previous_run_id: previousRunId,
        current_run_id: currentRunId,
        previous_run_number: previousRun?.run_number || 'Unknown',
        current_run_number: currentRun?.run_number || 'Unknown',
        differences: differences.sort((a, b) => Math.abs(b.change) - Math.abs(a.change)), // Sort by change magnitude
        summary
      };

    } catch (error) {
      console.error('Error fetching shortage diff:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch shortage comparison',
        variant: 'destructive',
      });
      throw error;
    }
  }, [toast]);

  return {
    // State
    isLoading,

    // Warehouse Operations
    fetchWarehouses,

    // Dashboard
    fetchDashboardMetrics,
    fetchMRPHistory,

    // MRP Operations
    runMRP,

    // Shortage Operations
    fetchShortages,
    acceptSuggestions,

    // Planned Orders
    fetchPlannedOrders,

    // Item Planning
    fetchItemPlanningParams,
    saveItemPlanningParams,
    fetchShortageDiff,
  };
}
